@file:NoLiveLiterals
@file:Suppress("UNCHECKED_CAST", "UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package com.example.borshi.web.wallet

import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.firebase.FirebaseFirestore
import com.example.borshi.web.firebase.FirebaseFunctions
import com.example.borshi.web.firebase.FirebaseService
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flow
import kotlin.js.json

/**
 * Firebase implementation of WalletRepository
 * Communicates with Firestore for reads and Cloud Functions for writes
 */
class FirebaseWalletRepository(
    private val firestore: dynamic,
    private val functions: dynamic
) : WalletRepository {

    private val walletsCache = mutableMapOf<String, MutableStateFlow<Wallet?>>()
    private val unsubscribers = mutableMapOf<String, () -> Unit>()

    override fun observeWallet(): StateFlow<Wallet?> {
        val userId = FirebaseService.getCurrentUserId()
        if (userId == null) return MutableStateFlow(null).asStateFlow()

        return walletsCache.getOrPut(userId) {
            val flow = MutableStateFlow<Wallet?>(null)

            try {
                // Subscribe to Firestore real-time updates - Wallet data is on the subcollection
                console.log("Observing wallet for user: $userId")
                // OLD: val docRef = FirebaseFirestore.doc(firestore, "users", userId)
                // NEW: /users/{userId}/wallet/balance
                val walletCollection = FirebaseFirestore.collection(firestore, "users/$userId/wallet")
                val docRef = FirebaseFirestore.doc(walletCollection, "balance")

                val unsubscribe = FirebaseFirestore.onSnapshot(docRef) { snapshot ->
                    try {
                        // Use .exists() method on the snapshot object directly
                        if (snapshot.exists() == true) {
                            val data = snapshot.data()
                            val wallet = parseWallet(userId, data)
                            flow.value = wallet
                        } else {
                            // Wallet doesn't exist yet (user hasn't added money)
                            console.log("Wallet document does not exist for $userId")
                            flow.value = null
                        }
                    } catch (e: Throwable) {
                        console.error("Error parsing wallet snapshot:", e)
                    }
                }

                unsubscribers[userId] = unsubscribe
            } catch (e: Throwable) {
                console.error("Error setting up wallet observer:", e)
            }

            flow
        }
    }

    override fun observeTransactions(
        limit: Int,
        startAfter: Long?
    ): Flow<List<WalletTransaction>> = flow {
        val userId = FirebaseService.getCurrentUserId() ?: return@flow emit(emptyList())
        // NEW (Aligned with Python script): /users/{userId}/wallet/balance/transactions
        val collectionRef = FirebaseFirestore.collection(firestore, "users/$userId/wallet/balance/transactions")

        var query = FirebaseFirestore.query(
            collectionRef,
            // Removed redundant userId filter since collection is already nested
            FirebaseFirestore.orderBy("timestamp", "desc"),
            FirebaseFirestore.limit(limit)
        )

        if (startAfter != null) {
            query = FirebaseFirestore.query(
                query,
                FirebaseFirestore.startAfter(startAfter)
            )
        }

        val snapshot = FirebaseFirestore.getDocs(query).await()
        // Access docs property array directly
        val docsArray = snapshot.docs as Array<dynamic>
        val transactions = docsArray.map { doc ->
            parseTransaction(doc.id as String, doc.data())
        }

        emit(transactions)
    }.catch { e ->
        console.error("Error observing transactions", e)
        emit(emptyList())
    }

    override suspend fun getTransaction(transactionId: String): Result<WalletTransaction> {
        return try {
            val userId = FirebaseService.getCurrentUserId() ?: return Result.failure(Exception("User not logged in"))
            val docRef = FirebaseFirestore.doc(firestore, "users/$userId/wallet/balance/transactions", transactionId)
            val snapshot = FirebaseFirestore.getDoc(docRef).await()

            if (snapshot.exists() == true) {
                val data = snapshot.data()
                // We're unsure of the userId here, but it's part of the data
                val transaction = parseTransaction(transactionId, data)
                Result.success(transaction)
            } else {
                Result.failure(Exception("Transaction not found"))
            }
        } catch (e: Exception) {
            console.error("Error getting transaction", e)
            Result.failure(e)
        }
    }

    override suspend fun requestTopUp(
        userId: String,
        amount: Money,
        idempotencyKey: String,
        paymentMethodId: String?
    ): Result<TopUpIntent> {
        return try {
            // CRITICAL: Convert Kotlin Long to JavaScript number
            // Kotlin Long is represented as an object in JS, not a primitive number
            val minorUnitsAsNumber = amount.minorUnits.toDouble().toInt()

            val callable = FirebaseFunctions.httpsCallable(functions, "createTopUpIntent")
            val result = callable(json(
                "amount" to json(
                    "currency" to amount.currency,
                    "minorUnits" to minorUnitsAsNumber
                ),
                "idempotencyKey" to idempotencyKey,
                "paymentMethodId" to paymentMethodId
            )).await()

            val data = result.data ?: result

            // Use local userId instead of trusting backend return for userId (though backend should return correct one)
            val intent = TopUpIntent(
                intentId = data.intentId as? String ?: "",
                userId = userId,
                amount = Money(
                    currency = data.amount?.currency as? String ?: amount.currency,
                    minorUnits = (data.amount?.minorUnits as? Number)?.toLong() ?: amount.minorUnits
                ),
                status = data.status as? String ?: "pending",
                paymentGatewayReference = data.paymentGatewayReference as? String,
                createdAt = (data.createdAt as? Number)?.toLong() ?: 0L,
                expiresAt = (data.expiresAt as? Number)?.toLong() ?: 0L
            )

            Result.success(intent)
        } catch (e: Exception) {
            console.error("Error requesting top-up", e)
            Result.failure(e)
        }
    }

    override suspend fun captureTopUp(topUpIntentId: String): Result<WalletTransaction> {
        return try {
            // Call Cloud Function to capture top-up
            val callable = FirebaseFunctions.httpsCallable(functions, "captureTopUp")
            val result = callable(json("topUpIntentId" to topUpIntentId)).await()

            val data = result.data.transaction
            val transaction = parseTransaction(data.id as String, data)

            Result.success(transaction)
        } catch (e: Exception) {
            console.error("Error capturing top-up", e)
            Result.failure(e)
        }
    }

    override suspend fun purchaseWithWallet(
        userId: String, // Keep param for local mapping, but DO NOT send to backend
        planId: String,
        amount: Money,
        idempotencyKey: String,
        metadata: Map<String, String>
    ): Result<WalletOperationResult> {
        return try {
            // Do not send userId; backend uses context.auth.uid
            val callable = FirebaseFunctions.httpsCallable(functions, "purchaseWithWallet")
            val result = callable(json(
                "planId" to planId,
                "amount" to json(
                    "currency" to amount.currency,
                    "minorUnits" to amount.minorUnits
                ),
                "idempotencyKey" to idempotencyKey,
                "metadata" to metadata.toJsObject()
            )).await()

            val data = result.data
            if (data.success == true) {
                val transaction = parseTransaction(data.transaction.id as String, data.transaction)
                val wallet = parseWallet(userId, data.wallet)
                Result.success(WalletOperationResult.Success(transaction, wallet))
            } else {
                val error = parseError(data.error as? String ?: "Unknown error")
                Result.success(WalletOperationResult.Failure(error))
            }
        } catch (e: Exception) {
            console.error("Error purchasing with wallet", e)
            Result.failure(e)
        }
    }

    override suspend fun refundToWallet(
        userId: String, // Keep param for local mapping, but DO NOT send to backend
        orderId: String,
        amount: Money,
        idempotencyKey: String,
        reason: String
    ): Result<WalletOperationResult> {
        return try {
            // Do not send userId; backend uses context.auth.uid
            val callable = FirebaseFunctions.httpsCallable(functions, "refundToWallet")
            val result = callable(json(
                "orderId" to orderId,
                "amount" to json(
                    "currency" to amount.currency,
                    "minorUnits" to amount.minorUnits
                ),
                "idempotencyKey" to idempotencyKey,
                "reason" to reason
            )).await()

            val data = result.data
            if (data.success == true) {
                val transaction = parseTransaction(data.transaction.id as String, data.transaction)
                val wallet = parseWallet(userId, data.wallet)
                Result.success(WalletOperationResult.Success(transaction, wallet))
            } else {
                val error = parseError(data.error as? String ?: "Unknown error")
                Result.success(WalletOperationResult.Failure(error))
            }
        } catch (e: Exception) {
            console.error("Error refunding to wallet", e)
            Result.failure(e)
        }
    }

    override suspend fun holdFunds(
        userId: String, // Keep param for local mapping, but DO NOT send to backend
        orderId: String,
        amount: Money,
        idempotencyKey: String
    ): Result<WalletOperationResult> {
        return try {
            // Do not send userId; backend uses context.auth.uid
            val callable = FirebaseFunctions.httpsCallable(functions, "holdFunds")
            val result = callable(json(
                "orderId" to orderId,
                "amount" to json(
                    "currency" to amount.currency,
                    "minorUnits" to amount.minorUnits
                ),
                "idempotencyKey" to idempotencyKey
            )).await()

            val data = result.data
            if (data.success == true) {
                val transaction = parseTransaction(data.transaction.id as String, data.transaction)
                val wallet = parseWallet(userId, data.wallet)
                Result.success(WalletOperationResult.Success(transaction, wallet))
            } else {
                val error = parseError(data.error as? String ?: "Unknown error")
                Result.success(WalletOperationResult.Failure(error))
            }
        } catch (e: Exception) {
            console.error("Error holding funds", e)
            Result.failure(e)
        }
    }

    override suspend fun releaseFunds(
        userId: String, // Keep param for local mapping, but DO NOT send to backend
        orderId: String,
        amount: Money,
        idempotencyKey: String
    ): Result<WalletOperationResult> {
        return try {
            // Do not send userId; backend uses context.auth.uid
            val callable = FirebaseFunctions.httpsCallable(functions, "releaseFunds")
            val result = callable(json(
                "orderId" to orderId,
                "amount" to json(
                    "currency" to amount.currency,
                    "minorUnits" to amount.minorUnits
                ),
                "idempotencyKey" to idempotencyKey
            )).await()

            val data = result.data
            if (data.success == true) {
                val transaction = parseTransaction(data.transaction.id as String, data.transaction)
                val wallet = parseWallet(userId, data.wallet)
                Result.success(WalletOperationResult.Success(transaction, wallet))
            } else {
                val error = parseError(data.error as? String ?: "Unknown error")
                Result.success(WalletOperationResult.Failure(error))
            }
        } catch (e: Exception) {
            console.error("Error releasing funds", e)
            Result.failure(e)
        }
    }

    override suspend fun captureFunds(
        userId: String, // Keep param for local mapping, but DO NOT send to backend
        orderId: String,
        amount: Money,
        idempotencyKey: String
    ): Result<WalletOperationResult> {
        return try {
            // Do not send userId; backend uses context.auth.uid
            val callable = FirebaseFunctions.httpsCallable(functions, "captureFunds")
            val result = callable(json(
                "orderId" to orderId,
                "amount" to json(
                    "currency" to amount.currency,
                    "minorUnits" to amount.minorUnits
                ),
                "idempotencyKey" to idempotencyKey
            )).await()

            val data = result.data
            if (data.success == true) {
                val transaction = parseTransaction(data.transaction.id as String, data.transaction)
                val wallet = parseWallet(userId, data.wallet)
                Result.success(WalletOperationResult.Success(transaction, wallet))
            } else {
                val error = parseError(data.error as? String ?: "Unknown error")
                Result.success(WalletOperationResult.Failure(error))
            }
        } catch (e: Exception) {
            console.error("Error capturing funds", e)
            Result.failure(e)
        }
    }

    /**
     * Parse Firestore wallet document to Wallet object
     */
    private fun parseWallet(userId: String, data: dynamic): Wallet {
        // Debug log to see what we are getting
        console.log("Parsing wallet data for $userId", data)

        val currency = data.currency as? String ?: "INR"

        val available = when {
            // Priority 1: availableBalance.minorUnits (most accurate - source of truth)
            data.availableBalance != null -> {
                val minorUnits = (data.availableBalance?.minorUnits as? Number)?.toLong()
                if (minorUnits != null && minorUnits != 0L) {
                    Money(
                        currency = data.availableBalance?.currency as? String ?: currency,
                        minorUnits = minorUnits
                    )
                } else {
                    // Fallback to currentBalance if minorUnits is 0 or null
                    val bal = (data.currentBalance as? Number)?.toDouble() ?: 0.0
                    Money.fromRupees(bal, currency)
                }
            }
            // Priority 2: currentBalance (legacy numeric field)
            data.currentBalance != null -> {
                val bal = (data.currentBalance as? Number)?.toDouble() ?: 0.0
                Money.fromRupees(bal, currency)
            }
            // Priority 3: walletBalance (very old legacy)
            data.walletBalance != null -> {
                val rawBalance = data.walletBalance
                val balance = (rawBalance as? Number)?.toDouble() ?: 0.0
                Money.fromRupees(balance, currency)
            }
            else -> Money.zero(currency)
        }

        val reserved = when {
            // Priority 1: reservedBalance.minorUnits (most accurate - source of truth)
            data.reservedBalance != null -> {
                val minorUnits = (data.reservedBalance?.minorUnits as? Number)?.toLong()
                if (minorUnits != null) {
                    Money(
                        currency = data.reservedBalance?.currency as? String ?: currency,
                        minorUnits = minorUnits
                    )
                } else {
                    // Fallback to reservedAmount if minorUnits is null
                    val res = (data.reservedAmount as? Number)?.toDouble() ?: 0.0
                    Money.fromRupees(res, currency)
                }
            }
            // Priority 2: reservedAmount (legacy numeric field)
            data.reservedAmount != null -> {
                val res = (data.reservedAmount as? Number)?.toDouble() ?: 0.0
                Money.fromRupees(res, currency)
            }
            else -> Money.zero(currency)
        }

        return Wallet(
            userId = userId,
            availableBalance = available,
            reservedBalance = reserved,
            currency = currency,
            createdAt = (data.createdAt as? Number)?.toLong() ?: 0L,
            updatedAt = (data.lastUpdated as? Number)?.toLong()
                ?: (data.updatedAt as? Number)?.toLong() ?: 0L,
            version = (data.version as? Number)?.toLong() ?: 0L
        )
    }

    private fun parseTransactionType(raw: String?): TransactionType {
        if (raw == null) return TransactionType.ADJUSTMENT

        // Map user's lowercase strings to Enum
        return when (raw.lowercase()) {
            "top_up" -> TransactionType.TOPUP
            "order_payment", "payment" -> TransactionType.PURCHASE
            "refund" -> TransactionType.REFUND
            "reward" -> TransactionType.ADJUSTMENT
            "hold" -> TransactionType.HOLD
            "release" -> TransactionType.RELEASE
            "capture" -> TransactionType.CAPTURE
            "withdrawal" -> TransactionType.WITHDRAWAL
            else -> TransactionType.values().find { it.name == raw.uppercase() } ?: TransactionType.ADJUSTMENT
        }
    }

    private fun parseTransactionStatus(raw: String?): TransactionStatus {
        return TransactionStatus.values().find { it.name == raw?.uppercase() } ?: TransactionStatus.SUCCEEDED
    }

    private fun parseReferenceType(raw: String?): ReferenceType {
        return ReferenceType.values().find { it.name == raw?.uppercase() } ?: ReferenceType.NONE
    }

    /**
     * Parse Firestore transaction document to WalletTransaction object
     */
    private fun parseTransaction(id: String, data: dynamic): WalletTransaction {
        // Handle amount which might be a simple Number (new schema) or Object (old schema)
        val amount = if (data.amount is Number) {
            val amtDouble = (data.amount as Number).toDouble()
            // Money class stores absolute value
            Money.fromRupees(kotlin.math.abs(amtDouble), "INR")
        } else if (data.amount != null) {
            Money(
                currency = data.amount?.currency as? String ?: "INR",
                minorUnits = (data.amount?.minorUnits as? Number)?.toLong() ?: 0L
            )
        } else {
            Money.zero("INR")
        }

        // Handle timestamps
        // User schema uses 'timestamp', existing uses 'createdAt'
        val created = (data.timestamp as? Number)?.toLong()
            ?: (data.createdAt as? Number)?.toLong()
            ?: 0L

        return WalletTransaction(
            id = id,
            userId = data.userId as? String ?: "",
            type = parseTransactionType(data.type as? String),
            status = parseTransactionStatus(data.status as? String),
            amount = amount,
            balanceAfter = null, // New schema implies computed on server, rarely stored on txn doc
            referenceType = parseReferenceType(data.referenceType as? String),
            referenceId = data.orderId as? String ?: data.referenceId as? String ?: "",
            idempotencyKey = data.idempotencyKey as? String ?: "",
            description = data.description as? String ?: "",
            metadata = dynamicToMap(data.metadata),
            createdAt = created,
            updatedAt = created // Transaction docs are usually immutable
        )
    }

    private fun dynamicToMap(dyn: dynamic): Map<String, String> {
        val map = mutableMapOf<String, String>()
        if (dyn != null) {
            val keys = js("Object").keys(dyn) as Array<String>
            for (key in keys) {
                val value = dyn[key]
                if (value != null) {
                    map[key] = value.toString()
                }
            }
        }
        return map
    }

    /**
     * Parse error string to WalletError
     */
    private fun parseError(errorCode: String): WalletError {
        return when (errorCode) {
            "insufficient_funds" -> WalletError.InsufficientFunds
            "wallet_not_found" -> WalletError.WalletNotFound
            "transaction_not_found" -> WalletError.TransactionNotFound
            "invalid_amount" -> WalletError.InvalidAmount
            "duplicate_transaction" -> WalletError.DuplicateTransaction
            else -> WalletError.UnknownError(errorCode)
        }
    }

    /**
     * Clean up listeners when repository is no longer needed
     */
    fun dispose() {
        unsubscribers.values.forEach { it() }
        unsubscribers.clear()
        walletsCache.clear()
    }
}

/**
 * Convert Kotlin Map to JavaScript object
 */
private fun Map<String, String>.toJsObject(): dynamic {
    val obj = js("{}")
    for ((key, value) in this) {
        obj[key] = value
    }
    return obj
}
